import React from 'react';
import { X, Plus, Minus, Trash2, ShoppingBag } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from './ui/sheet';
import { Button } from './ui/button';
import { Separator } from './ui/separator';
import { ScrollArea } from './ui/scroll-area';
import { WhatsAppButton } from './WhatsAppButton';
import { CouponBanner } from './CouponBanner';
import { toast } from 'sonner';

export const CartSheet = ({ isOpen, onClose, cartItems, onUpdateQuantity, onRemoveItem, cartTotal, onCheckout }) => {
  const navigate = useNavigate();
  const handleQuantityChange = (itemId, newQuantity) => {
    if (newQuantity < 1) return;
    onUpdateQuantity(itemId, newQuantity);
  };

  const handleRemoveItem = (item) => {
    onRemoveItem(item.id);
    toast.success(`${item.name} removed from cart`);
  };

  return (
    <Sheet open={isOpen} onOpenChange={onClose}>
      <SheetContent className="w-full sm:max-w-lg bg-[#FAF9F7] flex flex-col">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2 font-seasons text-2xl text-[#2C2C2C]">
            <ShoppingBag className="h-6 w-6 text-[#C4B5A0]" />
            Shopping Cart
            {cartItems.length > 0 && (
              <span className="text-sm font-montserrat text-[#8B7D6B]">({cartItems.length} items)</span>
            )}
          </SheetTitle>
        </SheetHeader>

        {cartItems.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center py-12">
            <ShoppingBag className="h-16 w-16 text-[#D4C4B0] mb-4" />
            <p className="font-montserrat text-[#8B7D6B] mb-6">Your cart is empty</p>
            <Button
              className="bg-[#C4B5A0] hover:bg-[#B0A090] text-white font-montserrat tracking-wider uppercase"
              onClick={onClose}
            >
              Continue Shopping
            </Button>
          </div>
        ) : (
          <>
            <ScrollArea className="flex-1 -mx-6 px-6">
              <div className="space-y-4 py-4">
                {cartItems.map((item) => (
                  <div key={item.id} className="bg-white rounded-lg p-4 shadow-sm">
                    <div className="flex gap-4">
                      {/* Product Image */}
                      <div className="w-20 h-24 flex-shrink-0 bg-[#F5F3F0] rounded overflow-hidden">
                        <img
                          src={item.image}
                          alt={item.name}
                          className="w-full h-full object-cover"
                        />
                      </div>

                      {/* Product Details */}
                      <div className="flex-1 min-w-0">
                        <div className="flex justify-between gap-2 mb-2">
                          <h4 className="font-montserrat text-sm text-[#2C2C2C] line-clamp-2">
                            {item.name}
                          </h4>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6 text-[#8B7D6B] hover:text-[#6B4E37] flex-shrink-0"
                            onClick={() => handleRemoveItem(item)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>

                        <p className="text-xs text-[#8B7D6B] font-montserrat mb-3">{item.category}</p>

                        <div className="flex items-center justify-between">
                          {/* Quantity Controls */}
                          <div className="flex items-center gap-2 border border-[#D4C4B0] rounded">
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-7 w-7 text-[#2C2C2C] hover:text-[#C4B5A0]"
                              onClick={() => handleQuantityChange(item.id, item.quantity - 1)}
                            >
                              <Minus className="h-3 w-3" />
                            </Button>
                            <span className="font-montserrat text-sm text-[#2C2C2C] w-8 text-center">
                              {item.quantity}
                            </span>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-7 w-7 text-[#2C2C2C] hover:text-[#C4B5A0]"
                              onClick={() => handleQuantityChange(item.id, item.quantity + 1)}
                            >
                              <Plus className="h-3 w-3" />
                            </Button>
                          </div>

                          {/* Price */}
                          <div className="text-right">
                            <p className="font-seasons text-lg text-[#2C2C2C]">₹{item.price * item.quantity}</p>
                            {item.quantity > 1 && (
                              <p className="text-xs text-[#8B7D6B] font-montserrat">₹{item.price} each</p>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </ScrollArea>

            {/* Cart Summary */}
            <div className="border-t border-[#D4C4B0] pt-4 space-y-3">
              <CouponBanner variant="cart" />
              <div className="flex justify-between items-center">
                <span className="font-montserrat text-[#8B7D6B] uppercase tracking-wider text-sm">Subtotal</span>
                <span className="font-seasons text-2xl text-[#2C2C2C]">₹{cartTotal}</span>
              </div>
              <p className="text-xs text-[#8B7D6B] font-montserrat">Shipping and taxes calculated at checkout</p>
              <Button
                className="w-full bg-[#C4B5A0] hover:bg-[#B0A090] text-white font-montserrat tracking-wider uppercase py-6 transition-all duration-300 hover:shadow-lg"
                onClick={() => {
                  onClose();
                  navigate('/checkout');
                }}
              >
                Proceed to Checkout
              </Button>
              <WhatsAppButton variant="cart" cartItems={cartItems} />
              <Button
                variant="outline"
                className="w-full border-[#C4B5A0] text-[#C4B5A0] hover:bg-[#F5F3F0] font-montserrat tracking-wider uppercase"
                onClick={onClose}
              >
                Continue Shopping
              </Button>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
};